import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LocaleContext = createContext();

const RTL_LANGUAGES = ['ar'];
const SUPPORTED = ['en', 'fr', 'ar'];

const applyLocale = (locale) => {
  const root = document.documentElement;
  const isRTL = RTL_LANGUAGES.includes(locale);
  root.setAttribute('lang', locale);
  root.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
};

export const LocaleProvider = ({ children }) => {
  const { i18n } = useTranslation();

  /**
   * i18next is the source of truth for which language is actually rendering.
   * Defaulting to 'en' here made the switcher advertise "EN" while the browser
   * detector had already resolved French, so the control disagreed with the
   * page. Fall back to whatever i18next resolved, not a hardcoded guess.
   */
  const [locale, setLocaleState] = useState(() => {
    const stored = localStorage.getItem('locale');
    if (stored && SUPPORTED.includes(stored)) return stored;
    const detected = String(i18n?.resolvedLanguage || i18n?.language || 'en').split('-')[0];
    return SUPPORTED.includes(detected) ? detected : 'en';
  });

  const setLocale = (newLocale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
    i18n.changeLanguage(newLocale);
    applyLocale(newLocale);
  };

  useEffect(() => {
    i18n.changeLanguage(locale);
    applyLocale(locale);
  }, []);

  // Keep the context in step if anything changes the language through i18next
  // directly, so the switcher can never drift out of sync with the page again.
  useEffect(() => {
    const onChanged = (lng) => {
      const base = String(lng || '').split('-')[0];
      if (SUPPORTED.includes(base)) {
        setLocaleState((prev) => (prev === base ? prev : base));
        applyLocale(base);
      }
    };
    i18n.on('languageChanged', onChanged);
    return () => i18n.off('languageChanged', onChanged);
  }, [i18n]);

  const isRTL = RTL_LANGUAGES.includes(locale);
  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <LocaleContext.Provider value={{ locale, setLocale, isRTL, dir }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
